export class SC_LocationServiceRecurrence {
	static readonly inputKeys = {
		internalID: 'id',
		parent: 'parent',
		h: 'h',
		m: 'm',
		rsh: 'rsh',
		rsm: 'rsm',
		reh: 'reh',
		rem: 'rem',
		days: 'days',
		techs: 'techs',
		dur: 'dur',
		crew: 'crew',
		desc: 'desc',
		auto: 'auto'
	};

	public id: number | null = null;
	public parent: number | null = null;
	public h: number | null = null;
	public m: number | null = null;
	public rsh: number | null = null;
	public rsm: number | null = null;
	public reh: number | null = null;
	public rem: number | null = null;
	public days: Set<number> | null = null;
	public techs: number | null = null;
	public dur: number | null = null;
	public crew: number | null = null;
	public desc: string | null = null;
	public auto: boolean | null = null;

	constructor({
		id = null,
		parent = null,
		h = null,
		m = null,
		rsh = null,
		rsm = null,
		reh = null,
		rem = null,
		days = null,
		techs = null,
		dur = null,
		crew = null,
		desc = null,
		auto = null
	} = {}) {
		this.id = id;
		this.parent = parent;
		this.h = h !== null && !isNaN(h) ? Number(h) : h;
		this.m = m !== null && !isNaN(m) ? Number(m) : m;
		this.rsh = rsh !== null && !isNaN(rsh) ? Number(rsh) : rsh;
		this.rsm = rsm !== null && !isNaN(rsm) ? Number(rsm) : rsm;
		this.reh = reh !== null && !isNaN(reh) ? Number(reh) : reh;
		this.rem = rem !== null && !isNaN(rem) ? Number(rem) : rem;
		this.days = days;
		this.techs = techs;
		this.dur = dur;
		this.crew = crew;
		this.desc = desc;
		this.auto = auto;
	}

	static fromAPI(jsonin: Record<string, any>): SC_LocationServiceRecurrence {
		let days: Set<number> | null = null;

		if (jsonin[this.inputKeys.days]) {
			days = new Set(
				jsonin[this.inputKeys.days].replace('*', '1,2,3,4,5,6,0').split(',').map(Number)
			);
		}

		return new SC_LocationServiceRecurrence({
			id: jsonin[this.inputKeys.internalID] ?? null,
			parent: jsonin[this.inputKeys.parent] ?? null,
			h: jsonin[this.inputKeys.h] ?? null,
			m: jsonin[this.inputKeys.m] ?? null,
			rsh: jsonin[this.inputKeys.rsh] ?? null,
			rsm: jsonin[this.inputKeys.rsm] ?? null,
			reh: jsonin[this.inputKeys.reh] ?? null,
			rem: jsonin[this.inputKeys.rem] ?? null,
			days: days,
			techs: jsonin[this.inputKeys.techs] ?? null,
			dur: jsonin[this.inputKeys.dur] ?? null,
			crew: jsonin[this.inputKeys.crew] ?? null,
			desc: jsonin[this.inputKeys.desc] ?? null,
			auto: jsonin[this.inputKeys.auto] ?? null,
		});
	}

	toAPI(): Record<string, any> {
		let daysin: Set<number> | null = this.days;
		let days: string | null = null;

		if (daysin) {
			if (daysin.size === 7) {
				days = '*';
			} else {
				days = Array.from(daysin)
					.map((day) => (day + 6) % 7)
					.sort()
					.map(day => (day + 1) % 7)
					.join(',');
			}
		}

		return {
			[SC_LocationServiceRecurrence.inputKeys.internalID]: this.id,
			[SC_LocationServiceRecurrence.inputKeys.parent]: this.parent,
			[SC_LocationServiceRecurrence.inputKeys.h]: this.h,
			[SC_LocationServiceRecurrence.inputKeys.m]: this.m,
			[SC_LocationServiceRecurrence.inputKeys.rsh]: this.rsh,
			[SC_LocationServiceRecurrence.inputKeys.rsm]: this.rsm,
			[SC_LocationServiceRecurrence.inputKeys.reh]: this.reh,
			[SC_LocationServiceRecurrence.inputKeys.rem]: this.rem,
			[SC_LocationServiceRecurrence.inputKeys.days]: days,
			[SC_LocationServiceRecurrence.inputKeys.techs]: this.techs,
			[SC_LocationServiceRecurrence.inputKeys.dur]: this.dur,
			[SC_LocationServiceRecurrence.inputKeys.crew]: this.crew,
			[SC_LocationServiceRecurrence.inputKeys.desc]: this.desc,
			[SC_LocationServiceRecurrence.inputKeys.auto]: this.auto,
		};
	}

	timeStartDisplay(): string | null {
		if (this.h !== null && this.m !== null) {
			const hour = this.h % 12 || 12;
			const minute = this.m.toString().padStart(2, '0');
			const ampm = this.h >= 12 ? 'PM' : 'AM';
			return `${hour}:${minute} ${ampm}`;
		}
		return null;
	}

	rangeStartDisplay(): string | null {
		if (this.rsh !== null && this.rsm !== null) {
			const hour = this.rsh % 12 || 12;
			const minute = this.rsm.toString().padStart(2, '0');
			const ampm = this.rsh >= 12 ? 'PM' : 'AM';
			return `${hour}:${minute} ${ampm}`;
		}
		return null;
	}

	valueTimeStart(): string {
		if (this.h !== null && this.m !== null) {
			return `${this.h.toString().padStart(2, '0')}:${this.m.toString().padStart(2, '0')}`;
		} else {
			return '';
		}
	}

	valueRangeStart(): string {
		if (this.rsh !== null && this.rsm !== null) {
			return `${this.rsh.toString().padStart(2, '0')}:${this.rsm.toString().padStart(2, '0')}`;
		} else {
			return '';
		}
	}

	timeEndDisplay(): string | null {
		if (this.h !== null && this.m !== null && this.dur !== null) {
			const offh = this.h + Math.floor((this.dur + this.m) / 60);
			const hour = offh % 12 || 12;
			const minute = ((this.m + this.dur) % 60).toString().padStart(2, '0');
			const ampm = offh >= 12 ? 'PM' : 'AM';
			return `${hour}:${minute} ${ampm}`;
		}
		return null;
	}

	rangeEndDisplay(): string | null {
		if (this.reh !== null && this.rem !== null) {
			const hour = this.reh % 12 || 12;
			const minute = this.rem.toString().padStart(2, '0');
			const ampm = this.reh >= 12 ? 'PM' : 'AM';
			return `${hour}:${minute} ${ampm}`;
		}
		return null;
	}

	valueTimeEnd(): string {
		if (this.h !== null && this.m !== null && this.dur !== null) {
			return `${this.hEnd.toString().padStart(2, '0')}:${this.mEnd.toString().padStart(2, '0')}`;
		}
		return '';
	}

	get mEnd() {
		return (this.m + this.dur) % 60;
	}

	get hEnd() {
		return this.h + Math.floor((this.dur + this.m) / 60);
	}

	valueRangeEnd(): string {
		if (this.reh !== null && this.rem !== null) {
			return `${this.reh.toString().padStart(2, '0')}:${this.rem.toString().padStart(2, '0')}`;
		} else {
			return '';
		}
	}

	clone(): SC_LocationServiceRecurrence {
		let days: Set<number> | null = null;

		if (this.days !== null) {
			days = new Set(this.days);
		}

		return new SC_LocationServiceRecurrence({
			id: this.id,
			parent: this.parent,
			h: this.h,
			m: this.m,
			rsh: this.rsh,
			rsm: this.rsm,
			reh: this.reh,
			rem: this.rem,
			days: days,
			techs: this.techs,
			dur: this.dur,
			crew: this.crew,
			desc: this.desc,
			auto: this.auto
		});
	}

	merge(other: SC_LocationServiceRecurrence): SC_LocationServiceRecurrence {
		if (other.id !== null) this.id = other.id;
		if (other.parent !== null) this.parent = other.parent;
		if (other.h !== null) this.h = other.h;
		if (other.m !== null) this.m = other.m;
		if (other.rsh !== null) if (other.rsh === false) this.rsh = null; else this.rsh = other.rsh;
		if (other.rsm !== null) if (other.rsm === false) this.rsm = null; else this.rsm = other.rsm;
		if (other.reh !== null) if (other.reh === false) this.reh = null; else this.reh = other.reh;
		if (other.rem !== null) if (other.rem === false) this.rem = null; else this.rem = other.rem;
		if (other.techs !== null) this.techs = other.techs;
		if (other.dur !== null) this.dur = other.dur;
		if (other.crew !== null) this.crew = other.crew;
		if (other.desc !== null) this.desc = other.desc;
		if (other.auto !== null) this.auto = other.auto;

		// Handle days set
		if (other.days !== null) {
			this.days = new Set(other.days);
		}

		return this;
	}

	get recurring(): boolean {
		return Boolean(this.auto && this.h !== null && this.m !== null && this.dur !== null && this.days.size);
	}

	get truthyTime(): boolean {
		return Number.isFinite(this.h) && Number.isFinite(this.m);
	}
}
